import React, { useState } from 'react';
import { Calculator, CheckCheck, CheckSquare, FileOutput, Link2, PackageSearch, Paperclip, Send, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { inquiriesAPI } from '../../services/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { InquiryDocumentPreviewDialog } from './InquiryDocumentPreviewDialog';
import { PartMatchDialog } from './PartMatchDialog';
import { ItemCostingChoiceDialog } from './ItemCostingChoiceDialog';
import { LastQuoteChip } from '../quotations/LastQuoteChip';

const rateToQuote = (product) => {
  const costing = product.linked_costing || product.suggested_costing;
  if (!costing) return Number(product.target_price || 0);
  if (product.quantity_unit === 'kg') {
    if (costing.selling_price_per_kg) return Number(costing.selling_price_per_kg);
    const weight = Number(product.net_weight_grams || 0);
    return weight ? (Number(costing.selling_price_per_piece || 0) * 1000) / weight : 0;
  }
  return Number(costing.selling_price_per_piece || 0);
};

export const InquiryItemsTab = ({ inquiry, selectedIds, setSelectedIds, onQuote, onVendorRfq, onRefresh }) => {
  const navigate = useNavigate();
  const [matchItem, setMatchItem] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [busyItem, setBusyItem] = useState(null);
  const [costingItem, setCostingItem] = useState(null);
  const [approvingAll, setApprovingAll] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const deleteItem = async (product) => {
    const label = product.internal_part_no || product.customer_part_no || product.description;
    if (!window.confirm(`Remove "${label}" from this inquiry? Parts, costings and quotations are not touched.`)) return;
    setBusyItem(product.id);
    try {
      await inquiriesAPI.deleteItem(inquiry.id, product.id);
      toast.success('Item removed');
      setSelectedIds(selectedIds.filter((id) => id !== product.id));
      await onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not remove this item', { duration: 8000 });
    } finally { setBusyItem(null); }
  };

  const deleteSelected = async () => {
    if (!window.confirm(`Remove ${selectedIds.length} selected item(s) from this inquiry? Items already costed, quoted or ordered are skipped.`)) return;
    setDeleting(true);
    try {
      const response = await inquiriesAPI.bulkDeleteItems(inquiry.id, selectedIds);
      const { deleted_count: deletedCount, skipped } = response.data;
      if (deletedCount) toast.success(`${deletedCount} item(s) removed`);
      if (skipped?.length) {
        toast.error(`${skipped.length} kept: ${skipped.map((item) => `${item.label || item.item_id} (${item.reason})`).join(' · ')}`, { duration: 12000 });
      }
      setSelectedIds([]);
      await onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk delete failed');
    } finally { setDeleting(false); }
  };
  const products = inquiry.products || [];
  const documents = inquiry.documents || [];
  const pendingIds = products.filter((item) => item.costing_state === 'pending_approval').map((item) => item.id);
  const allSelected = products.length > 0 && selectedIds.length === products.length;
  const boughtOutIds = products.filter((item) => item.sourcing_type === 'outsourced').map((item) => item.id);

  const toggle = (id) => setSelectedIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  const toggleAll = () => setSelectedIds(allSelected ? [] : products.map((item) => item.id));

  const setSourcing = async (product, boughtOut) => {
    setBusyItem(product.id);
    try {
      await inquiriesAPI.updateItem(inquiry.id, product.id, {
        sourcing_type: boughtOut ? 'outsourced' : 'in_house',
      });
      toast.success(boughtOut ? 'Marked as bought-out' : 'Marked as in-house');
      await onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update sourcing');
    } finally { setBusyItem(null); }
  };

  const saveQuantity = async (product, raw) => {
    const value = Number(String(raw).replace(/,/g, ''));
    if (!raw || Number.isNaN(value) || value <= 0) {
      toast.error('Enter a quantity greater than zero');
      return;
    }
    if (value === product.quantity) return;
    setBusyItem(product.id);
    try {
      await inquiriesAPI.updateItem(inquiry.id, product.id, { quantity: value });
      toast.success('Quantity updated');
      await onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update the quantity');
    } finally { setBusyItem(null); }
  };

  const setUnit = async (product, unit) => {
    setBusyItem(product.id);
    try {
      await inquiriesAPI.updateItem(inquiry.id, product.id, { quantity_unit: unit });
      toast.success(unit === 'kg' ? 'Quantity is now in kilograms' : 'Quantity is now in pieces');
      await onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update the unit');
    } finally { setBusyItem(null); }
  };

  const rfqForBoughtOut = () => {
    if (!boughtOutIds.length) {
      toast.error('Mark at least one item as bought-out first');
      return;
    }
    setSelectedIds(boughtOutIds);
    onVendorRfq();
  };

  const approveCosting = async (product, costingId) => {
    setBusyItem(product.id);
    try {
      const response = await inquiriesAPI.approveItemCosting(inquiry.id, product.id, costingId);
      toast.success(response.data.message);
      setCostingItem(null);
      await onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not approve this costing');
    } finally { setBusyItem(null); }
  };

  const approveAll = async () => {
    setApprovingAll(true);
    try {
      const response = await inquiriesAPI.approveSuggestedCostings(inquiry.id, pendingIds);
      toast.success(response.data.message);
      await onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not approve the suggested costings');
    } finally { setApprovingAll(false); }
  };

  return (
    <div className="space-y-5" data-testid="inquiry-items-tab">
      <div className="flex flex-col gap-3 border-b pb-5 lg:flex-row lg:items-center lg:justify-between">
        <div><h2 className="text-lg font-heading font-bold">Items, sourcing & costing</h2>
          <p className="text-sm text-muted-foreground">Mark bought-out items, send vendor RFQs for them, and cost or quote the rest.</p></div>
        <div className="flex flex-wrap gap-2">
          {pendingIds.length > 0 && (
            <Button variant="outline" className="border-amber-400 text-amber-900" onClick={approveAll} disabled={approvingAll}
              data-testid="approve-all-suggested-costings-button">
              <CheckCheck className="mr-2 h-4 w-4" /> Approve {pendingIds.length} costing{pendingIds.length === 1 ? '' : 's'}
            </Button>
          )}
          <Button variant="outline" onClick={() => setSelectedIds(products.map((item) => item.id))} disabled={!products.length} data-testid="select-complete-inquiry-button">
            <CheckSquare className="mr-2 h-4 w-4" /> Select complete inquiry
          </Button>
          <Button variant="outline" onClick={rfqForBoughtOut} disabled={!boughtOutIds.length} data-testid="rfq-bought-out-items-button">
            <Send className="mr-2 h-4 w-4" /> RFQ for bought-out ({boughtOutIds.length})
          </Button>
          <Button variant="outline" onClick={onVendorRfq} disabled={!selectedIds.length} data-testid="generate-selected-vendor-rfq-button">
            <Send className="mr-2 h-4 w-4" /> Vendor RFQ
          </Button>
          <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={deleteSelected} disabled={!selectedIds.length || deleting} data-testid="delete-selected-inquiry-items-button">
            <Trash2 className="mr-2 h-4 w-4" /> Delete selected ({selectedIds.length})
          </Button>
          <Button onClick={onQuote} disabled={!selectedIds.length} data-testid="quote-selected-inquiry-items-button">
            <FileOutput className="mr-2 h-4 w-4" /> Review & quote selected
          </Button>
        </div>
      </div>
      {products.length === 0 ? (
        <div className="border border-dashed py-14 text-center" data-testid="inquiry-items-empty">
          <PackageSearch className="mx-auto mb-3 h-9 w-9 text-muted-foreground" />
          <p className="font-medium">No items identified yet.</p><p className="text-sm text-muted-foreground">Process an inquiry document or add items from Part Master.</p>
        </div>
      ) : (
        <div className="overflow-x-auto border">
          <Table><TableHeader><TableRow>
            <TableHead className="w-12"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-inquiry-items-checkbox" /></TableHead>
            <TableHead>Part number</TableHead><TableHead>Description</TableHead><TableHead>Qty</TableHead>
            <TableHead>Bought-out</TableHead><TableHead>Documents</TableHead><TableHead>Costing</TableHead>
            <TableHead>Last quoted</TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow></TableHeader>
          <TableBody>{products.map((product) => {
            const itemDocuments = documents.filter((document) => document.inquiry_product_id === product.id);
            return (
              <TableRow key={product.id} data-testid={`inquiry-item-row-${product.id}`}>
                <TableCell><Checkbox checked={selectedIds.includes(product.id)} onCheckedChange={() => toggle(product.id)} data-testid={`select-inquiry-item-${product.id}`} /></TableCell>
                <TableCell className="font-mono font-medium" data-testid={`inquiry-item-part-number-${product.id}`}>{product.internal_part_no || product.customer_part_no || 'Unlinked'}</TableCell>
                <TableCell className="min-w-[240px]" data-testid={`inquiry-item-description-${product.id}`}>{product.description}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      className="h-7 w-24 font-mono"
                      defaultValue={product.quantity ?? ''}
                      placeholder="Qty"
                      key={`${product.id}-${product.quantity}`}
                      disabled={busyItem === product.id}
                      onBlur={(event) => saveQuantity(product, event.target.value)}
                      onKeyDown={(event) => { if (event.key === 'Enter') event.target.blur(); }}
                      data-testid={`inquiry-item-qty-${product.id}`}
                    />
                    <Select
                      value={product.quantity_unit === 'kg' ? 'kg' : 'pcs'}
                      onValueChange={(value) => setUnit(product, value)}
                      disabled={busyItem === product.id}
                    >
                      <SelectTrigger className="h-7 w-[74px]" data-testid={`inquiry-item-unit-${product.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pcs">pcs</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {product.quantity === null || product.quantity === undefined ? (
                    <p className="mt-1 text-xs text-amber-700" data-testid={`inquiry-item-qty-missing-${product.id}`}>
                      Quantity needed to quote
                    </p>
                  ) : product.quantity_unit === 'kg' && product.net_weight_grams ? (
                    <p className="mt-1 text-xs text-muted-foreground" data-testid={`inquiry-item-implied-pcs-${product.id}`}>
                      ≈ {Math.round((Number(product.quantity || 0) * 1000) / product.net_weight_grams).toLocaleString('en-IN')} pcs
                    </p>
                  ) : null}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch checked={product.sourcing_type === 'outsourced'} disabled={busyItem === product.id}
                      onCheckedChange={(value) => setSourcing(product, value)}
                      data-testid={`bought-out-toggle-${product.id}`} />
                    <Badge variant="outline" data-testid={`sourcing-badge-${product.id}`}>
                      {product.sourcing_type === 'outsourced' ? 'Bought-out' : 'In-house'}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell>
                  {itemDocuments.length === 0 ? <span className="text-xs text-muted-foreground">—</span> : (
                    <div className="flex flex-wrap gap-1" data-testid={`item-documents-${product.id}`}>
                      {itemDocuments.slice(0, 3).map((document) => (
                        <Button key={document.id} size="sm" variant="ghost" className="h-7 px-2 text-xs"
                          onClick={() => setPreviewing(document)} title={document.filename}
                          data-testid={`view-item-document-${document.id}`}>
                          <Paperclip className="mr-1 h-3 w-3" />{document.filename.slice(0, 14)}
                        </Button>
                      ))}
                      {itemDocuments.length > 3 && <span className="self-center text-xs text-muted-foreground">+{itemDocuments.length - 3}</span>}
                    </div>
                  )}
                </TableCell>
                <TableCell data-testid={`inquiry-item-costing-${product.id}`}>
                  {product.costing_state === 'approved' ? (
                    <div className="space-y-1">
                      <Badge className="bg-emerald-100 text-emerald-800" data-testid={`costing-approved-badge-${product.id}`}>
                        {product.linked_costing?.costing_number}
                      </Badge>
                      <p className="text-xs text-muted-foreground">
                        ₹{Number(product.linked_costing?.selling_price_per_piece || 0).toFixed(4)}/pc
                        {product.linked_costing?.selling_price_per_kg
                          ? ` · ₹${Number(product.linked_costing.selling_price_per_kg).toFixed(2)}/kg` : ''}
                      </p>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
                          onClick={() => navigate(`/costings/${product.linked_costing.id}`)}
                          data-testid={`open-item-costing-${product.id}`}>Open</Button>
                        {product.costing_options?.length >= 1 && (
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
                            onClick={() => setCostingItem(product)}
                            data-testid={`change-item-costing-${product.id}`}>Versions</Button>
                        )}
                      </div>
                    </div>
                  ) : product.costing_state === 'pending_approval' ? (
                    <div className="space-y-1">
                      <Badge className="bg-amber-100 text-amber-900" data-testid={`costing-pending-badge-${product.id}`}>
                        {product.suggested_costing?.costing_number} found
                      </Badge>
                      <p className="text-xs text-muted-foreground">
                        ₹{Number(product.suggested_costing?.selling_price_per_piece || 0).toFixed(4)}/pc · awaiting approval
                      </p>
                      <div className="flex gap-1">
                        <Button size="sm" className="h-7 px-2 text-xs" disabled={busyItem === product.id}
                          onClick={() => approveCosting(product, product.suggested_costing.id)}
                          data-testid={`approve-item-costing-${product.id}`}>
                          <CheckCheck className="mr-1 h-3 w-3" /> Approve
                        </Button>
                        {product.costing_options?.length >= 1 && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                            onClick={() => setCostingItem(product)}
                            data-testid={`choose-item-costing-${product.id}`}>Choose</Button>
                        )}
                      </div>
                    </div>
                  ) : product.part_master_id ? (
                    <Button size="sm" variant="outline" onClick={() => navigate(`/costings/new?part=${product.part_master_id}&inquiry=${inquiry.id}&product=${product.id}`)} data-testid={`cost-inquiry-item-${product.id}`}>
                      <Calculator className="mr-2 h-4 w-4" /> Cost item
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setMatchItem(product)} data-testid={`link-inquiry-item-part-${product.id}`}>
                      <Link2 className="mr-2 h-4 w-4" /> Link to Part Master
                    </Button>
                  )}
                </TableCell>
                <TableCell className="min-w-[170px]" data-testid={`inquiry-item-last-quote-cell-${product.id}`}>
                  <LastQuoteChip
                    lastQuote={product.last_quote}
                    newRate={rateToQuote(product)}
                    testId={`inquiry-item-last-quote-${product.id}`}
                  />
                </TableCell>
                <TableCell>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-600"
                    onClick={() => deleteItem(product)} disabled={busyItem === product.id}
                    title="Remove this item from the inquiry"
                    data-testid={`delete-inquiry-item-${product.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}</TableBody></Table>
        </div>
      )}
      <ItemCostingChoiceDialog
        item={costingItem}
        inquiryId={inquiry.id}
        open={Boolean(costingItem)}
        onOpenChange={(value) => !value && setCostingItem(null)}
        busy={busyItem === costingItem?.id}
        onApprove={(costingId) => approveCosting(costingItem, costingId)}
      />
      <PartMatchDialog
        inquiryId={inquiry.id}
        item={matchItem}
        open={Boolean(matchItem)}
        onOpenChange={(value) => !value && setMatchItem(null)}
        onLinked={onRefresh}
      />
      <InquiryDocumentPreviewDialog inquiryId={inquiry.id} document={previewing}
        open={Boolean(previewing)} onOpenChange={(value) => !value && setPreviewing(null)} />
    </div>
  );
};
